import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Wrench, Plus, Search, Calendar, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = {
  maintenance: { label: "Maintenance", color: "bg-blue-100 text-blue-800" },
  reparation: { label: "Réparation", color: "bg-orange-100 text-orange-800" },
  garantie: { label: "Garantie", color: "bg-green-100 text-green-800" },
  depannage: { label: "Dépannage", color: "bg-red-100 text-red-800" },
  mise_en_service: { label: "Mise en service", color: "bg-purple-100 text-purple-800" },
};

const STATUTS = {
  planifiee: { label: "Planifiée", color: "bg-blue-100 text-blue-800" },
  en_cours: { label: "En cours", color: "bg-orange-100 text-orange-800" },
  terminee: { label: "Terminée", color: "bg-green-100 text-green-800" },
  annulee: { label: "Annulée", color: "bg-gray-100 text-gray-800" },
};

const SAVPage = ({ user }) => {
  const [interventions, setInterventions] = useState([]);
  const [projets, setProjets] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  
  const emptyIntervention = {
    projet_id: "",
    client_id: "",
    type_intervention: "maintenance",
    titre: "",
    description_probleme: "",
    date_prevue: "",
    statut: "planifiee",
    urgence: false,
    cout_main_oeuvre_ht: 0,
    cout_pieces_ht: 0,
    cout_deplacement_ht: 0,
  };
  
  const [formData, setFormData] = useState(emptyIntervention);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem("token");
      const [int, proj, cli] = await Promise.all([
        axios.get(`${API}/interventions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setInterventions(int.data);
      setProjets(proj.data);
      setClients(cli.data);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/interventions`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Intervention créée !");
      setShowDialog(false);
      fetchAll();
      setFormData(emptyIntervention);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la création");
    }
  };

  const filteredInterventions = interventions.filter((inter) =>
    inter.titre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: interventions.length,
    planifiees: interventions.filter(i => i.statut === "planifiee").length,
    en_cours: interventions.filter(i => i.statut === "en_cours").length,
    terminées: interventions.filter(i => i.statut === "terminee").length,
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.prenom} ${client.nom}` : "Client inconnu";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Wrench className="w-8 h-8 text-red-600" />
            SAV & Interventions
          </h1>
          <p className="text-slate-600 mt-1">Gérez les interventions après-vente</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle intervention
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Total", value: stats.total, color: "text-slate-800" },
          { label: "Planifiées", value: stats.planifiees, color: "text-blue-600" },
          { label: "En cours", value: stats.en_cours, color: "text-orange-600" },
          { label: "Terminées", value: stats.terminées, color: "text-green-600" },
        ].map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="pt-6">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <p className="text-sm text-slate-600">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher une intervention..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredInterventions.map((inter) => {
          const TypeConfig = TYPES[inter.type_intervention] || TYPES.maintenance;
          const StatutConfig = STATUTS[inter.statut] || STATUTS.planifiee;
          
          return (
            <Card key={inter.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Wrench className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-semibold">{inter.titre}</h3>
                      <Badge className={TypeConfig.color}>{TypeConfig.label}</Badge>
                      <Badge className={StatutConfig.color}>{StatutConfig.label}</Badge>
                      {inter.urgence && (
                        <Badge className="bg-red-500 text-white">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Urgent
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                      <div>
                        <p className="text-slate-500">Client</p>
                        <p className="font-medium">{getClientName(inter.client_id)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Date prévue</p>
                        <p className="font-medium">
                          {inter.date_prevue ? new Date(inter.date_prevue).toLocaleDateString('fr-FR') : "Non planifiée"}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Coût total HT</p>
                        <p className="font-bold text-red-600">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(inter.cout_total_ht || 0)}
                        </p>
                      </div>
                    </div>

                    {inter.description_probleme && (
                      <p className="mt-3 text-sm text-slate-600">{inter.description_probleme}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filteredInterventions.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center text-slate-500">
              <Wrench className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune intervention trouvée</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer une nouvelle intervention</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <select
                  value={formData.type_intervention}
                  onChange={(e) => setFormData({...formData, type_intervention: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {Object.entries(TYPES).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Projet *</Label>
                <select
                  value={formData.projet_id}
                  onChange={(e) => {
                    const projet = projets.find(p => p.id === e.target.value);
                    setFormData({...formData, projet_id: e.target.value, client_id: projet?.client_id || ""});
                  }}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="">Sélectionner un projet</option>
                  {projets.map(p => (
                    <option key={p.id} value={p.id}>{p.titre}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
                placeholder="Ex: Maintenance annuelle installation PV"
              />
            </div>

            <div>
              <Label>Description du problème</Label>
              <textarea
                value={formData.description_probleme}
                onChange={(e) => setFormData({...formData, description_probleme: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date prévue</Label>
                <Input
                  type="date"
                  value={formData.date_prevue}
                  onChange={(e) => setFormData({...formData, date_prevue: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={formData.urgence}
                  onChange={(e) => setFormData({...formData, urgence: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Intervention urgente</Label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Main d'œuvre HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cout_main_oeuvre_ht}
                  onChange={(e) => setFormData({...formData, cout_main_oeuvre_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Pièces HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cout_pieces_ht}
                  onChange={(e) => setFormData({...formData, cout_pieces_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Déplacement HT (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cout_deplacement_ht}
                  onChange={(e) => setFormData({...formData, cout_deplacement_ht: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-red-600 hover:bg-red-700"
                disabled={!formData.projet_id || !formData.titre}
              >
                Créer l'intervention
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SAVPage;
